<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-avatar
          size="38px"
          color="warning"
          rounded
          v-on="on"
          v-ripple
          class="mr-2"
          @click="dialog = true"
        >
          <v-icon color="white">mdi-tools</v-icon>
        </v-avatar>
      </template>
      <span>Support Tools</span>
    </v-tooltip>

    <CustomDialog
      v-if="dialog"
      @close="dialog = false"
      title="Support Tools"
      width="l"
      height="auto"
      showCloseBtn
      hideCloseBtn
      fullscreen
      hideActionBtn
      :persistent="false"
    >
      <template v-slot:content>
        <!-- Tabs for different support sections -->
        <v-tabs v-model="activeTab">
          <v-tab>
            <v-icon left>mdi-cog-outline</v-icon>
            Remote Config
          </v-tab>
          <v-tab>
            <v-icon left>mdi-information-outline</v-icon>
            Systeminfo
          </v-tab>
          <v-tab v-if="hasOrganizationContext">
            <v-icon left>mdi-domain</v-icon>
            Organisation
          </v-tab>
          <v-tab v-if="hasOrganizationContext">
            <v-icon left>mdi-shield-account</v-icon>
            Berechtigungen
          </v-tab>
          <v-tab v-if="hasOrganizationContext && isActualSupportUser">
            <v-icon left>mdi-account-switch</v-icon>
            Impersonation
          </v-tab>
          <v-tab>
            <v-icon left>mdi-wrench</v-icon>
            Aktionen
          </v-tab>
        </v-tabs>

        <!-- Tab Content -->
        <v-tabs-items v-model="activeTab">
          <!-- Remote Config Tab -->
          <v-tab-item>
            <v-card-text class="pa-0">
              <div class="d-flex justify-space-between align-center pa-3">
                <h4>Remote Config Parameters</h4>
                <v-btn
                  small
                  @click="refreshConfig"
                  :loading="refreshLoading"
                  color="primary"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Neu laden
                </v-btn>
              </div>

              <v-list v-if="configValues.length > 0" nav>
                <div v-for="(config, index) in configValues" :key="config.key">
                  <v-list-item
                    link
                    class="my-1"
                    @click="copyToClipboard(config.key, config.value)"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ config.key }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span class="grey--text">
                          {{ config.source }}
                        </span>
                        <v-chip
                          small
                          label
                          :color="getValueColor(config.value)"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ config.value }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        @click="copyToClipboard(config.key, config.value)"
                      >
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider
                    v-if="index < configValues.length - 1"
                    :key="`divider-${index}`"
                  ></v-divider>
                </div>
              </v-list>

              <div
                v-else-if="!configLoading"
                class="pa-4 text-center grey--text"
              >
                <v-icon large color="grey">mdi-cog-off</v-icon>
                <div class="mt-2">Keine Remote Config Daten verfügbar</div>
              </div>

              <div v-if="configLoading" class="pa-4 text-center">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="40"
                ></v-progress-circular>
                <div class="mt-2 grey--text">Lade Remote Config...</div>
              </div>
            </v-card-text>
          </v-tab-item>

          <!-- System Info Tab -->
          <v-tab-item>
            <v-card-text class="pa-0">
              <v-list nav>
                <v-list-item
                  link
                  class="my-1"
                  @click="copyToClipboard('App Version', appVersion)"
                >
                  <v-list-item-content>
                    <v-list-item-title>App Version</v-list-item-title>
                    <v-list-item-subtitle>{{
                      appVersion
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="copyToClipboard('App Version', appVersion)"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item class="my-1">
                  <v-list-item-content>
                    <v-list-item-title>Umgebung</v-list-item-title>
                    <v-list-item-subtitle>
                      <template v-if="environment === 'production'">
                        <v-chip small color="red" text-color="white">
                          <v-icon left small>mdi-rocket</v-icon>
                          {{ environment }}
                        </v-chip>
                      </template>
                      <template v-else>
                        {{ environment }}
                      </template>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item
                  link
                  class="my-1"
                  @click="
                    copyToClipboard('Firebase Project', firebaseProjectId)
                  "
                >
                  <v-list-item-content>
                    <v-list-item-title>Firebase Projekt</v-list-item-title>
                    <v-list-item-subtitle>{{
                      firebaseProjectId
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="
                        copyToClipboard('Firebase Project', firebaseProjectId)
                      "
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item
                  link
                  class="my-1"
                  @click="copyToClipboard('User Agent', userAgent)"
                >
                  <v-list-item-content>
                    <v-list-item-title>User Agent</v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">{{
                      userAgent
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="copyToClipboard('User Agent', userAgent)"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item
                  link
                  class="my-1"
                  @click="copyToClipboard('Current Route', currentRoute)"
                >
                  <v-list-item-content>
                    <v-list-item-title>Aktuelle Route</v-list-item-title>
                    <v-list-item-subtitle>{{
                      currentRoute
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="copyToClipboard('Current Route', currentRoute)"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item class="my-1">
                  <v-list-item-content>
                    <v-list-item-title
                      >Version Check Umgehung</v-list-item-title
                    >
                    <v-list-item-subtitle>
                      <v-chip
                        small
                        :color="versionCheckBypass ? 'orange' : 'green'"
                        text-color="white"
                      >
                        {{ versionCheckBypass ? "Aktiviert" : "Deaktiviert" }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-tab-item>

          <!-- Organization Details Tab -->
          <v-tab-item v-if="hasOrganizationContext">
            <v-card-text class="pa-0">
              <v-list v-if="organization" nav>
                <!-- Organization Info -->
                <v-subheader>Organisation</v-subheader>
                <v-list-item
                  link
                  class="my-1"
                  @click="copyToClipboard('Organization ID', organizationId)"
                >
                  <v-list-item-content>
                    <v-list-item-title>ID</v-list-item-title>
                    <v-list-item-subtitle class="text-wrap">{{
                      organizationId
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="
                        copyToClipboard('Organization ID', organizationId)
                      "
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item
                  link
                  class="my-1"
                  @click="
                    copyToClipboard(
                      'Organization Name',
                      organization.displayName,
                    )
                  "
                >
                  <v-list-item-content>
                    <v-list-item-title>Name</v-list-item-title>
                    <v-list-item-subtitle>{{
                      organization.displayName
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      @click="
                        copyToClipboard(
                          'Organization Name',
                          organization.displayName,
                        )
                      "
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Config Section -->
                <v-subheader>Konfiguration</v-subheader>
                <v-list-item class="my-1">
                  <v-list-item-content>
                    <v-list-item-title>Wartungsmodus</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip
                        small
                        :color="
                          organization.config.inMaintenance ? 'orange' : 'green'
                        "
                        text-color="white"
                      >
                        {{
                          organization.config.inMaintenance
                            ? "Aktiv"
                            : "Inaktiv"
                        }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Extensions Section -->
                <v-subheader>Aktivierte Erweiterungen</v-subheader>
                <v-list-item class="my-1">
                  <v-list-item-content>
                    <div
                      v-if="
                        organization.config.extensions.activated &&
                        organization.config.extensions.activated.length > 0
                      "
                    >
                      <v-chip
                        v-for="ext in organization.config.extensions.activated"
                        :key="ext"
                        small
                        class="ma-1"
                        color="blue"
                        text-color="white"
                      >
                        {{ ext }}
                      </v-chip>
                    </div>
                    <div v-else class="grey--text">
                      Keine Erweiterungen aktiviert
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Features Section -->
                <v-subheader>Aktivierte Features</v-subheader>
                <v-list-item class="my-1">
                  <v-list-item-content>
                    <div
                      v-if="
                        organization.config.features.enabled &&
                        organization.config.features.enabled.length > 0
                      "
                    >
                      <v-chip
                        v-for="feature in organization.config.features.enabled"
                        :key="feature"
                        small
                        class="ma-1"
                        color="purple"
                        text-color="white"
                      >
                        {{ feature }}
                      </v-chip>
                    </div>
                    <div v-else class="grey--text">
                      Keine Features aktiviert
                    </div>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Billing Info -->
                <v-subheader>Abrechnung</v-subheader>
                <v-list-item class="my-1">
                  <v-list-item-content>
                    <v-list-item-title>Trial Status</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip
                        small
                        :color="
                          organization.billing.trial.isActive
                            ? 'orange'
                            : 'green'
                        "
                        text-color="white"
                      >
                        {{
                          organization.billing.trial.isActive
                            ? "Trial aktiv"
                            : "Regulär"
                        }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <div v-else class="pa-4 text-center grey--text">
                <v-icon large color="grey">mdi-domain-off</v-icon>
                <div class="mt-2">Keine Organisationsdaten verfügbar</div>
              </div>
            </v-card-text>
          </v-tab-item>

          <!-- User Permissions Tab -->
          <v-tab-item v-if="hasOrganizationContext">
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-3">
                <h4>Berechtigungen des Benutzers</h4>
                <v-chip small>{{ userPermissions.length }} aktiv</v-chip>
              </div>

              <!-- Permission Sources Legend -->
              <div class="mb-3">
                <v-chip x-small color="blue" text-color="white" class="mr-1"
                  >Gruppe</v-chip
                >
                <v-chip x-small color="purple" text-color="white" class="mr-1"
                  >Rolle</v-chip
                >
                <v-chip x-small color="orange" text-color="white"
                  >Individuell</v-chip
                >
              </div>

              <v-expansion-panels v-if="membership" accordion flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-account-group</v-icon>
                      Gruppen-Berechtigungen
                      <v-chip
                        x-small
                        class="ml-2"
                        color="blue"
                        text-color="white"
                      >
                        {{ groupPermissions.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="groupPermissions.length > 0">
                      <v-chip
                        v-for="perm in groupPermissions"
                        :key="perm"
                        small
                        class="ma-1"
                        outlined
                        @click="copyToClipboard('Permission', perm)"
                      >
                        {{ getPermissionLabel(perm) }}
                      </v-chip>
                    </div>
                    <div v-else class="text-caption grey--text">
                      Keine Gruppen-Berechtigungen
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-shield-account</v-icon>
                      Rollen-Berechtigungen
                      <v-chip
                        x-small
                        class="ml-2"
                        color="purple"
                        text-color="white"
                      >
                        {{ rolePermissions.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="rolePermissions.length > 0">
                      <v-chip
                        v-for="perm in rolePermissions"
                        :key="perm"
                        small
                        class="ma-1"
                        outlined
                        @click="copyToClipboard('Permission', perm)"
                      >
                        {{ getPermissionLabel(perm) }}
                      </v-chip>
                    </div>
                    <div v-else class="text-caption grey--text">
                      Keine Rollen-Berechtigungen
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-account</v-icon>
                      Individuelle Berechtigungen
                      <v-chip
                        x-small
                        class="ml-2"
                        color="orange"
                        text-color="white"
                      >
                        {{ individualPermissions.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="individualPermissions.length > 0">
                      <v-chip
                        v-for="perm in individualPermissions"
                        :key="perm"
                        small
                        class="ma-1"
                        outlined
                        @click="copyToClipboard('Permission', perm)"
                      >
                        {{ getPermissionLabel(perm) }}
                      </v-chip>
                    </div>
                    <div v-else class="text-caption grey--text">
                      Keine individuellen Berechtigungen
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-divider class="my-4"></v-divider>

              <h4 class="mb-3">Alle Berechtigungen (Baumansicht)</h4>
              <PermissionTreeView
                v-if="userPermissions.length > 0"
                :selected-permissions="userPermissions"
                :group-permissions="groupPermissions"
                :role-permissions="rolePermissions"
                :individual-permissions="individualPermissions"
                readOnly
                showSearch
                showSummary
                showPermissionSource
              />

              <div v-else class="pa-4 text-center grey--text">
                <v-icon large color="grey">mdi-shield-off</v-icon>
                <div class="mt-2">Keine Berechtigungen verfügbar</div>
              </div>
            </v-card-text>
          </v-tab-item>

          <!-- Impersonation Tab -->
          <v-tab-item v-if="hasOrganizationContext && isActualSupportUser">
            <v-card-text>
              <div class="mb-4">
                <h4 class="mb-3">Benutzer-Impersonation</h4>
                <p class="text-caption grey--text">
                  Wähle einen Benutzer aus, um die Ansicht mit dessen
                  Berechtigungen zu sehen.
                </p>
              </div>

              <v-alert
                v-if="impersonationActive"
                type="warning"
                dense
                class="mb-3"
              >
                <strong>Impersonation aktiv:</strong> Du siehst die Ansicht von
                {{ impersonatedUserName }}
              </v-alert>

              <v-autocomplete
                v-model="selectedImpersonationUserId"
                :items="organizationMembers"
                item-text="displayText"
                item-value="uid"
                label="Benutzer auswählen"
                prepend-icon="mdi-account-search"
                :loading="loadingMembers"
                :disabled="loadingMembers"
                clearable
                dense
                outlined
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-icon>mdi-account</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{
                      item.displayName
                    }}</v-list-item-title>
                    <v-list-item-subtitle>{{
                      item.email
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <v-row class="mt-4">
                <v-col cols="6">
                  <v-btn
                    block
                    color="primary"
                    :disabled="
                      !selectedImpersonationUserId || impersonationActive
                    "
                    @click="startImpersonation"
                  >
                    <v-icon left>mdi-account-switch</v-icon>
                    Impersonation starten
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    block
                    color="warning"
                    :disabled="!impersonationActive"
                    @click="stopImpersonation"
                  >
                    <v-icon left>mdi-account-cancel</v-icon>
                    Impersonation beenden
                  </v-btn>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <div class="text-caption grey--text">
                <v-icon small left color="grey">mdi-information</v-icon>
                Die Impersonation ändert nur die Berechtigungen für die Ansicht.
                Alle Aktionen werden weiterhin mit deinem Account durchgeführt.
              </div>
            </v-card-text>
          </v-tab-item>

          <!-- Actions Tab -->
          <v-tab-item>
            <v-card-text>
              <div class="mb-4">
                <h4 class="mb-3">Version Check Aktionen</h4>
                <v-row>
                  <v-col cols="6">
                    <v-btn
                      block
                      :color="versionCheckBypass ? 'warning' : 'success'"
                      @click="toggleVersionCheckBypass"
                    >
                      <v-icon left>{{
                        versionCheckBypass ? "mdi-eye-off" : "mdi-eye"
                      }}</v-icon>
                      {{ versionCheckBypass ? "Deaktivieren" : "Aktivieren" }}
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn block color="info" @click="testVersionDialog">
                      <v-icon left>mdi-test-tube</v-icon>
                      Dialog testen
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-4"></v-divider>

              <div class="mb-4">
                <h4 class="mb-3">Cache Aktionen</h4>
                <v-row>
                  <v-col cols="6">
                    <v-btn block color="warning" @click="clearBrowserCache">
                      <v-icon left>mdi-cached</v-icon>
                      Cache löschen
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn block color="error" @click="hardReload">
                      <v-icon left>mdi-refresh</v-icon>
                      Neu laden
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-4"></v-divider>

              <div class="mb-4">
                <h4 class="mb-3">Debug Aktionen</h4>
                <v-row>
                  <v-col cols="6">
                    <v-btn block color="primary" @click="exportSystemInfo">
                      <v-icon left>mdi-download</v-icon>
                      Info exportieren
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn block color="secondary" @click="openDevTools">
                      <v-icon left>mdi-code-braces</v-icon>
                      Dev Tools
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </CustomDialog>

    <!-- Snackbar for copy feedback -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="2000"
      bottom
      :color="snackbar.color"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import PermissionTreeView from "@/components/_systemwide/PermissionTreeView.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import { remoteConfig } from "@/firebase";
import {
  isSupportBypassEnabled,
  enableSupportBypass,
  disableSupportBypass,
} from "@/util/versionCheck";
import { db } from "@/firebase";
import { permissionsTree } from "@/data/permission-tree.js";

export default {
  name: "AppBarSupportTools",
  components: {
    PermissionTreeView,
    CustomDialog,
  },
  data() {
    return {
      dialog: false,
      activeTab: 0,
      configLoading: false,
      refreshLoading: false,
      configValues: [],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      // Impersonation
      selectedImpersonationUserId: null,
      organizationMembers: [],
      loadingMembers: false,
    };
  },
  computed: {
    appVersion() {
      return process.env.VUE_APP_VERSION || "Unknown";
    },
    environment() {
      return process.env.NODE_ENV || "Unknown";
    },
    firebaseProjectId() {
      return process.env.VUE_APP_FIREBASE_PROJECT_ID || "Unknown";
    },
    userAgent() {
      return navigator.userAgent;
    },
    currentRoute() {
      return this.$route.path;
    },
    versionCheckBypass() {
      return isSupportBypassEnabled();
    },
    // Organization context
    hasOrganizationContext() {
      return !!this.$route.params.organizationId;
    },
    organizationId() {
      return this.$route.params.organizationId || null;
    },
    organization() {
      return this.$store.state.organization?.activeOrganization || null;
    },
    membership() {
      return this.$store.state.organization?.membership || null;
    },
    userPermissions() {
      return this.membership?.access?.permissions?.allPermissionsIds || [];
    },
    groupPermissions() {
      return this.membership?.access?.permissions?.groupPermissionsIds || [];
    },
    rolePermissions() {
      // Role permissions = all - group - individual
      const all = this.userPermissions;
      const group = this.groupPermissions;
      const individual = this.individualPermissions;
      return all.filter((p) => !group.includes(p) && !individual.includes(p));
    },
    individualPermissions() {
      return (
        this.membership?.access?.permissions?.individualPermissionsIds || []
      );
    },
    isActualSupportUser() {
      return this.$store.getters["organization/checkIfActualSupport"] || false;
    },
    impersonationActive() {
      return this.$store.state.organization?.supportViewAsNormalUser || false;
    },
    impersonatedUserName() {
      return (
        this.$store.state.organization?.impersonatedUserName || "Unbekannt"
      );
    },
  },
  mounted() {
    // Restore impersonation state from sessionStorage on mount
    this.restoreImpersonationState();
  },
  watch: {
    dialog(newVal) {
      if (newVal && this.activeTab === 0) {
        this.loadRemoteConfig();
      }
      // Load members when opening impersonation tab
      if (newVal && this.hasOrganizationContext && this.isActualSupportUser) {
        this.loadOrganizationMembers();
      }
    },
    activeTab(newVal) {
      if (this.dialog && newVal === 0) {
        this.loadRemoteConfig();
      }
    },
  },
  methods: {
    async loadRemoteConfig() {
      this.configLoading = true;
      this.configValues = [];

      try {
        // Check if remoteConfig is available
        if (!remoteConfig) {
          throw new Error("Remote Config nicht verfügbar");
        }

        // Fetch and activate remote config
        await remoteConfig.fetchAndActivate();

        // Get all parameter keys we're interested in
        const parameterKeys = ["web_minimumAppVersion", "web_forceUpdate"];

        this.configValues = parameterKeys.map((key) => {
          const value = remoteConfig.getValue(key);
          return {
            key,
            value: value.asString(),
            source: this.getSource(value.getSource()),
          };
        });

        // Sort by parameter name
        this.configValues.sort((a, b) => a.key.localeCompare(b.key));
      } catch (error) {
        console.error("Error loading Remote Config:", error);
        this.showSnackbar("Fehler beim Laden der Remote Config", "error");
      } finally {
        this.configLoading = false;
      }
    },

    async refreshConfig() {
      this.refreshLoading = true;
      try {
        // Force fetch from server (bypass cache)
        remoteConfig.settings.minimumFetchIntervalMillis = 0;
        await this.loadRemoteConfig();
        // Reset fetch interval to default
        remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
        this.showSnackbar("Remote Config aktualisiert", "success");
      } catch (error) {
        console.error("Error refreshing Remote Config:", error);
        this.showSnackbar("Fehler beim Aktualisieren", "error");
      } finally {
        this.refreshLoading = false;
      }
    },

    getSource(source) {
      switch (source) {
        case "remote":
          return "Remote (Server)";
        case "default":
          return "Standard (Lokal)";
        case "static":
          return "Statisch";
        default:
          return "Unbekannt";
      }
    },

    getValueColor(value) {
      // Color coding for common values
      if (value === "true") return "orange";
      if (value === "false") return "green";
      if (value.match(/^\d+\.\d+\.\d+$/)) return "blue"; // Version format
      return "grey";
    },

    async copyToClipboard(key, value) {
      try {
        const textToCopy = `${key}: ${value}`;
        await navigator.clipboard.writeText(textToCopy);
        this.showSnackbar("In Zwischenablage kopiert", "success");
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        this.showSnackbar("Kopieren fehlgeschlagen", "error");
      }
    },

    toggleVersionCheckBypass() {
      if (this.versionCheckBypass) {
        disableSupportBypass();
        this.showSnackbar("Version Check Bypass deaktiviert", "success");
      } else {
        enableSupportBypass();
        this.showSnackbar("Version Check Bypass aktiviert", "warning");
      }
    },

    testVersionDialog() {
      // This would trigger the version dialog for testing
      // You might want to emit an event to parent or use a method to trigger it
      this.showSnackbar(
        "Version Dialog Test - Feature noch nicht implementiert",
        "info",
      );
    },

    clearBrowserCache() {
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });
        this.showSnackbar("Browser Cache geleert", "success");
      } else {
        this.showSnackbar("Cache API nicht verfügbar", "warning");
      }
    },

    hardReload() {
      // Clear cache and force reload
      this.clearBrowserCache();
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    },

    exportSystemInfo() {
      const systemInfo = {
        appVersion: this.appVersion,
        environment: this.environment,
        firebaseProjectId: this.firebaseProjectId,
        userAgent: this.userAgent,
        currentRoute: this.currentRoute,
        versionCheckBypass: this.versionCheckBypass,
        remoteConfig: this.configValues,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };

      const blob = new Blob([JSON.stringify(systemInfo, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mira-support-info-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.showSnackbar("System Info exportiert", "success");
    },

    openDevTools() {
      // This won't work in production, but shows the intent
      if (process.env.NODE_ENV === "development") {
        console.log("Development Tools Console");
        this.showSnackbar("Prüfe Browser Konsole", "info");
      } else {
        this.showSnackbar("F12 drücken für Developer Tools", "info");
      }
    },

    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    async loadOrganizationMembers() {
      if (!this.organizationId || this.organizationMembers.length > 0) {
        return;
      }

      this.loadingMembers = true;
      try {
        const snapshot = await db
          .collection("organizations")
          .doc(this.organizationId)
          .collection("membershipsPrivate")
          .where("access.status", "==", "activated")
          .get();

        this.organizationMembers = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            uid: doc.id,
            displayName: data.user?.displayName || "Unbekannt",
            email: data.user?.email || "",
            displayText: `${data.user?.displayName || "Unbekannt"} (${
              data.user?.email || ""
            })`,
          };
        });

        // Sort by display name
        this.organizationMembers.sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );
      } catch (error) {
        console.error("Error loading organization members:", error);
        this.showSnackbar("Fehler beim Laden der Mitglieder", "error");
      } finally {
        this.loadingMembers = false;
      }
    },

    async startImpersonation() {
      if (!this.selectedImpersonationUserId) {
        return;
      }

      try {
        // Load the membership of the selected user
        const membershipDoc = await db
          .collection("organizations")
          .doc(this.organizationId)
          .collection("membershipsPrivate")
          .doc(this.selectedImpersonationUserId)
          .get();

        if (!membershipDoc.exists) {
          this.showSnackbar("Mitgliedschaft nicht gefunden", "error");
          return;
        }

        const membershipData = membershipDoc.data();
        const impersonatedPermissions = {
          allPermissionsIds:
            membershipData.access?.permissions?.allPermissionsIds || [],
          groupPermissionsIds:
            membershipData.access?.permissions?.groupPermissionsIds || [],
          individualPermissionsIds:
            membershipData.access?.permissions?.individualPermissionsIds || [],
        };
        const impersonatedUserName =
          this.organizationMembers.find(
            (m) => m.uid === this.selectedImpersonationUserId,
          )?.displayName || "Unbekannt";

        // Store impersonation data to sessionStorage for persistence
        const impersonationData = {
          impersonatedPermissions,
          impersonatedUserId: this.selectedImpersonationUserId,
          impersonatedUserName,
          organizationId: this.organizationId,
        };
        sessionStorage.setItem(
          "supportImpersonation",
          JSON.stringify(impersonationData),
        );

        // Store original permissions first, then apply impersonated permissions
        this.$store.commit("organization/STORE_ORIGINAL_PERMISSIONS");
        this.$store.commit(
          "organization/APPLY_IMPERSONATED_PERMISSIONS",
          impersonatedPermissions,
        );
        this.$store.commit(
          "organization/SET_IMPERSONATED_USER_NAME",
          impersonatedUserName,
        );
        this.$store.commit(
          "organization/SET_SUPPORT_VIEW_AS_NORMAL_USER",
          true,
        );

        this.showSnackbar(
          `Impersonation als ${impersonatedUserName} gestartet`,
          "success",
        );
        this.dialog = false;
      } catch (error) {
        console.error("Error starting impersonation:", error);
        this.showSnackbar("Fehler beim Starten der Impersonation", "error");
      }
    },

    async stopImpersonation() {
      try {
        // Clear impersonation from sessionStorage
        sessionStorage.removeItem("supportImpersonation");

        // Restore original permissions and clear impersonation state
        this.$store.commit("organization/RESTORE_ORIGINAL_PERMISSIONS");
        this.$store.commit("organization/SET_IMPERSONATED_USER_NAME", null);
        this.$store.commit(
          "organization/SET_SUPPORT_VIEW_AS_NORMAL_USER",
          false,
        );
        this.selectedImpersonationUserId = null;

        this.showSnackbar("Impersonation beendet", "success");
        this.dialog = false;
      } catch (error) {
        console.error("Error stopping impersonation:", error);
        this.showSnackbar("Fehler beim Beenden der Impersonation", "error");
      }
    },

    getPermissionLabel(permissionId) {
      const findPermission = (items, id) => {
        for (const item of items) {
          if (item.id === id) {
            return item.chipsName || item.name || id;
          }
          if (item.children) {
            const found = findPermission(item.children, id);
            if (found) return found;
          }
        }
        return null;
      };
      return findPermission(permissionsTree, permissionId) || permissionId;
    },

    restoreImpersonationState() {
      // Check if there's persisted impersonation data
      const storedData = sessionStorage.getItem("supportImpersonation");
      if (!storedData) return;

      try {
        const impersonationData = JSON.parse(storedData);

        // Verify we're in the same organization
        if (impersonationData.organizationId !== this.organizationId) {
          sessionStorage.removeItem("supportImpersonation");
          return;
        }

        // Store original permissions first, then apply impersonated permissions
        this.$store.commit("organization/STORE_ORIGINAL_PERMISSIONS");
        this.$store.commit(
          "organization/APPLY_IMPERSONATED_PERMISSIONS",
          impersonationData.impersonatedPermissions,
        );
        this.$store.commit(
          "organization/SET_IMPERSONATED_USER_NAME",
          impersonationData.impersonatedUserName,
        );
        this.$store.commit(
          "organization/SET_SUPPORT_VIEW_AS_NORMAL_USER",
          true,
        );
        this.selectedImpersonationUserId = impersonationData.impersonatedUserId;
      } catch (error) {
        console.error("Error restoring impersonation state:", error);
        sessionStorage.removeItem("supportImpersonation");
      }
    },
  },
};
</script>
